import { Injectable, inject } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Device, DeviceId } from '@capacitor/device';
import { catchError, map, Observable, of, switchMap, throwError } from 'rxjs';

// Environment
import { environment } from '../../../../environments/environment';

// Types
import { ConfigFile, FailedCall } from '../../../shared/types/manageCalls';
import { StorageKey } from '../../../shared/types/storage';

// Http
import { ApiService } from '../../http/api.service';

// Services
import { LoggerService } from '../logger/logger.service';
import { NetworkService } from '../network/network.service';
import { StorageService } from '../storage/storage.service';

@Injectable({ providedIn: 'root' })
export class CommonService {
  private api = inject(ApiService);
  private logger = inject(LoggerService);
  private network = inject(NetworkService);
  private storage = inject(StorageService);

  public currentLang: string;
  public UUID: string;
  public PLATFORM: string;
  public configFile: ConfigFile;

  getPlatform(): void {
    if (environment.NEED_TO_FORCE_PLATFORM) {
      this.PLATFORM = environment.PLATFORM_TO_FORCE;
    } else {
      if (environment.ENV !== 'LOCALHOST') {
        this.PLATFORM = Capacitor.getPlatform();
      } else {
        this.PLATFORM = 'web';
      }
    }

    this.logger.consoleLog('CommonService', 'getPlatform', this.PLATFORM);
  }

  getUniqueDeviceID(): void {
    Device.getId()
      .then(({ identifier }: DeviceId) => {
        this.UUID = identifier;
        this.storage.setOnStorage(StorageKey.UUID, identifier).subscribe();
      })
      .catch(() => {
        this.logger.consoleError('CommonService', 'getUniqueDeviceID');
        this.UUID = 'ERR';
        this.storage.setOnStorage(StorageKey.UUID, 'ERR').subscribe();
      });
  }

  getConfigFile(): Observable<ConfigFile> {
    this.logger.consoleLog('CommonService', 'getConfigFile');
    return this.storage.getFromStorage<ConfigFile>(StorageKey.ConfigFile).pipe(
      switchMap((configFile: ConfigFile) => {
        if (
          !configFile ||
          !configFile.MORE_INFO ||
          !configFile.TERMS_AND_CONDITIONS ||
          configFile?.CONFIG_FILE_EXPIRATION.getTime() <= Date.now() ||
          configFile?.NUMBER_OF_INSERT_ACTIVATIONCODE_TRIAL !== undefined ||
          configFile?.ACTIVATIONCODE_URL_BUY !== undefined
        ) {
          this.logger.consoleLog('CommonService', 'getConfigFile', 'Need to download new CONFIG');

          return this.api.config.configFile().pipe(
            map((cfgFile: ConfigFile) => {
              cfgFile.CONFIG_FILE_EXPIRATION = new Date(cfgFile.CONFIG_FILE_EXPIRATION as unknown as string);

              this.configFile = cfgFile;
              this.storage.setOnStorage(StorageKey.ConfigFile, cfgFile).subscribe();
              return cfgFile;
            }),
            catchError((error: FailedCall) => {
              this.network.blockedApp = true;
              this.logger.consoleError('CommonService', 'getConfigFile', { error });
              return throwError(() => error);
            }),
          );
        } else {
          this.configFile = configFile;
          return of(configFile);
        }
      }),
    );
  }
}
